import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiMessageSquare,
  FiRefreshCw,
  FiRotateCw,
  FiSearch,
  FiSend,
  FiSlash,
  FiX,
} from "react-icons/fi";
import axios from "../api/axiosInstance";

const TABS = [
  { key: "needs_reply", label: "Needs reply" },
  { key: "critical_mentions", label: "Critical mentions" },
  { key: "needs_reanalysis", label: "Needs reanalysis" },
  { key: "dismissed", label: "Dismissed" },
];

const DEFAULT_FILTERS = {
  search: "",
  source_id: "all",
  reply_priority: "all",
  ai_status: "all",
  date_from: "",
  date_to: "",
};

function toQueryString(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "" || value === "all") return;
    search.append(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}

function formatDate(value) {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatScore(value, scale) {
  const numericValue = Number(value);
  const numericScale = Number(scale);
  if (!Number.isFinite(numericValue) || !Number.isFinite(numericScale) || numericScale <= 0) {
    return "No score";
  }
  return `${numericValue}/${numericScale}`;
}

function formatCriticality(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Not scored";
  return `${Math.round(numeric)}/100`;
}

function formatPriority(value) {
  switch (String(value || "").toLowerCase()) {
    case "high":
      return "High";
    case "medium":
      return "Medium";
    case "low":
      return "Low";
    default:
      return "Review";
  }
}

function buildExcerpt(review) {
  const value = [
    review?.latest_manager_summary,
    review?.latest_ai_summary,
    review?.title,
    review?.review_text,
    review?.negative_text,
    review?.positive_text,
  ]
    .map((item) => String(item || "").trim())
    .find(Boolean);

  if (!value) return "Open the review to read the guest message and suggested next steps.";
  return value.length > 170 ? `${value.slice(0, 167)}...` : value;
}

function getReplyLabel(status) {
  switch (String(status || "").toLowerCase()) {
    case "responded":
      return "Answered";
    case "recommended":
      return "Recommended";
    case "draft_ready":
      return "Draft ready";
    case "dismissed":
      return "Dismissed";
    default:
      return "Pending";
  }
}

function getReplyTone(status) {
  switch (String(status || "").toLowerCase()) {
    case "responded":
      return "success";
    case "recommended":
    case "draft_ready":
      return "warning";
    case "dismissed":
      return "muted";
    default:
      return "neutral";
  }
}

function getAnalysisLabel(status) {
  switch (String(status || "").toLowerCase()) {
    case "done":
      return "Ready";
    case "processing":
      return "In progress";
    case "error":
      return "Needs review";
    default:
      return "Waiting";
  }
}

function getAnalysisTone(status) {
  switch (String(status || "").toLowerCase()) {
    case "done":
      return "success";
    case "processing":
      return "warning";
    case "error":
      return "danger";
    default:
      return "neutral";
  }
}

function buildToneClass(prefix, tone) {
  return `${prefix} ${prefix}--${tone}`;
}

function formatJson(value) {
  try {
    return JSON.stringify(value || {}, null, 2);
  } catch (_error) {
    return String(value || "");
  }
}

function Modal({ open, title, subtitle, onClose, children }) {
  if (!open) return null;
  return (
    <div className="ops-modalBackdrop" onClick={onClose}>
      <div
        className="ops-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="ops-modalHeader">
          <div>
            <h2 className="ops-modalTitle">{title}</h2>
            {subtitle ? <p className="ops-modalSubtitle">{subtitle}</p> : null}
          </div>
          <button type="button" className="ops-iconBtn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>
        <div className="ops-modalBody">{children}</div>
      </div>
    </div>
  );
}

function ReviewOperationsPage() {
  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id") || "";
  const [businessUnit, setBusinessUnit] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [activeTab, setActiveTab] = useState("needs_reply");
  const [sources, setSources] = useState([]);
  const [tabCounts, setTabCounts] = useState({
    needs_reply: 0,
    critical_mentions: 0,
    needs_reanalysis: 0,
    dismissed: 0,
  });
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total_items: 0,
    total_pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const [selectedReviewIds, setSelectedReviewIds] = useState([]);
  const [selectedReviewDetail, setSelectedReviewDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [replyDraft, setReplyDraft] = useState("");
  const [workflowNotes, setWorkflowNotes] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const selectAllRef = useRef(null);

  const fetchQueue = async ({ tab = activeTab, page = 1, silent = false, nextFilters } = {}) => {
    const effectiveFilters = nextFilters || filters;
    silent ? setRefreshing(true) : setLoading(true);
    setError("");

    try {
      const query = toQueryString({
        business_unit_id: businessUnitId || undefined,
        tab,
        page,
        search: effectiveFilters.search,
        source_id: effectiveFilters.source_id,
        reply_priority: effectiveFilters.reply_priority,
        ai_status: effectiveFilters.ai_status,
        date_from: effectiveFilters.date_from,
        date_to: effectiveFilters.date_to,
      });
      const response = await axios.get(`/api/reviews-intelligence/operations/queue${query}`);
      setBusinessUnit(response.data?.business_unit || null);
      setSources(Array.isArray(response.data?.sources) ? response.data.sources : []);
      setTabCounts(response.data?.tabs || {});
      setReviews(Array.isArray(response.data?.reviews) ? response.data.reviews : []);
      setPagination(response.data?.pagination || {});
      setActiveTab(response.data?.active_tab || tab);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "Error loading review operations.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setFilters(DEFAULT_FILTERS);
    setSelectedReviewId(null);
    setSelectedReviewIds([]);
    setSelectedReviewDetail(null);
    setDetailError("");
    fetchQueue({ tab: "needs_reply", page: 1, nextFilters: DEFAULT_FILTERS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId]);

  useEffect(() => {
    setSelectedReviewIds((current) =>
      current.filter((reviewId) => reviews.some((review) => review.id === reviewId))
    );
  }, [reviews]);

  const fetchReviewDetail = async (reviewId) => {
    setDetailLoading(true);
    setDetailError("");

    try {
      const query = toQueryString({ business_unit_id: businessUnitId || undefined });
      const response = await axios.get(`/api/reviews-intelligence/reviews/${reviewId}${query}`);
      const detail = response.data?.review || null;
      setSelectedReviewDetail(detail);
      setReplyDraft(
        detail?.reply_tracking?.draft_text ||
          detail?.latest_ai_analysis?.suggested_reply_text ||
          detail?.property_reply_text ||
          ""
      );
      setWorkflowNotes(detail?.reply_tracking?.manual_notes || "");
    } catch (requestError) {
      setDetailError(requestError?.response?.data?.error || "Error loading review detail.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOpenDetail = async (reviewId) => {
    setSelectedReviewId(reviewId);
    setSelectedReviewDetail(null);
    setReplyDraft("");
    setWorkflowNotes("");
    await fetchReviewDetail(reviewId);
  };

  const closeDetail = () => {
    setSelectedReviewId(null);
    setSelectedReviewDetail(null);
    setReplyDraft("");
    setWorkflowNotes("");
    setDetailLoading(false);
    setDetailError("");
  };

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const applyFilters = async () => {
    await fetchQueue({ tab: activeTab, page: 1, nextFilters: filters });
  };

  const handleTabChange = async (tabKey) => {
    if (tabKey === activeTab) return;
    await fetchQueue({ tab: tabKey, page: 1, nextFilters: filters });
  };

  const handlePageChange = async (nextPage) => {
    if (nextPage < 1 || nextPage > (pagination.total_pages || 1) || nextPage === pagination.page) {
      return;
    }
    await fetchQueue({ tab: activeTab, page: nextPage, silent: true });
  };

  const refreshAfterAction = async (reviewId = selectedReviewId) => {
    await fetchQueue({ tab: activeTab, page: pagination.page || 1, silent: true });
    if (reviewId) {
      await fetchReviewDetail(reviewId);
    }
  };

  const runWorkflowAction = async (action, extraPayload = {}, successMessage) => {
    if (!selectedReviewId) return;
    setActionLoading(action);
    setFeedback("");
    setError("");
    setDetailError("");

    try {
      await axios.patch(
        `/api/reviews-intelligence/operations/reviews/${selectedReviewId}/workflow`,
        {
          business_unit_id: businessUnitId,
          action,
          draft_text: replyDraft,
          manual_notes: workflowNotes,
          ...extraPayload,
        }
      );
      setFeedback(successMessage);
      await refreshAfterAction(selectedReviewId);
    } catch (requestError) {
      setDetailError(requestError?.response?.data?.error || "Error updating the review workflow.");
    } finally {
      setActionLoading("");
    }
  };

  const handleAnalyzeNow = async () => {
    if (!selectedReviewId) return;
    setActionLoading("reanalyze");
    setFeedback("");
    setDetailError("");
    const hasPreviousAnalysis = Boolean(selectedReviewDetail?.latest_ai_analysis?.id);

    try {
      await axios.post(`/api/reviews-intelligence/reviews/${selectedReviewId}/reanalyze`, {
        business_unit_id: businessUnitId,
        process_now: true,
      });
      setFeedback(
        hasPreviousAnalysis
          ? "Review analyzed again with the latest prompt."
          : "Review sent to AI analysis."
      );
      await refreshAfterAction(selectedReviewId);
    } catch (requestError) {
      setDetailError(requestError?.response?.data?.error || "Error sending the review to AI.");
    } finally {
      setActionLoading("");
    }
  };

  const currentRangeText = useMemo(() => {
    const total = pagination.total_items || 0;
    if (!total) return "No reviews in this queue.";
    const start = ((pagination.page || 1) - 1) * (pagination.page_size || 20) + 1;
    const end = Math.min(start + (pagination.page_size || 20) - 1, total);
    return `${start}-${end} of ${total} reviews`;
  }, [pagination]);

  const selectedVisibleReviews = useMemo(
    () => reviews.filter((review) => selectedReviewIds.includes(review.id)),
    [reviews, selectedReviewIds]
  );

  const allVisibleSelected = Boolean(reviews.length) && selectedVisibleReviews.length === reviews.length;
  const someVisibleSelected = selectedVisibleReviews.length > 0 && !allVisibleSelected;

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someVisibleSelected;
  }, [someVisibleSelected]);

  const bulkAnalyzeLabel = useMemo(() => {
    if (!selectedVisibleReviews.length) return "Analyze selected";
    const analyzedCount = selectedVisibleReviews.filter((review) => review.has_ai_analysis).length;
    if (analyzedCount === 0) return "Analyze selected";
    if (analyzedCount === selectedVisibleReviews.length) return "Reanalyze selected";
    return "Analyze / reanalyze selected";
  }, [selectedVisibleReviews]);

  const toggleReviewSelection = (reviewId) => {
    setSelectedReviewIds((current) =>
      current.includes(reviewId)
        ? current.filter((currentId) => currentId !== reviewId)
        : [...current, reviewId]
    );
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedReviewIds((current) =>
        current.filter((reviewId) => !reviews.some((review) => review.id === reviewId))
      );
      return;
    }

    const visibleIds = reviews.map((review) => review.id);
    setSelectedReviewIds((current) => Array.from(new Set([...current, ...visibleIds])));
  };

  const handleBulkAnalyze = async () => {
    if (!selectedReviewIds.length) return;
    setActionLoading("bulk_ai");
    setFeedback("");
    setError("");

    try {
      const response = await axios.post("/api/reviews-intelligence/operations/reviews/bulk-ai", {
        business_unit_id: businessUnitId,
        review_ids: selectedReviewIds,
        process_now: true,
      });
      setFeedback(
        response.data?.message ||
          `${selectedReviewIds.length} reviews sent to AI analysis.`
      );
      await fetchQueue({ tab: activeTab, page: pagination.page || 1, silent: true });
      if (selectedReviewId && selectedReviewIds.includes(selectedReviewId)) {
        await fetchReviewDetail(selectedReviewId);
      }
      setSelectedReviewIds([]);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "Error sending selected reviews to AI.");
    } finally {
      setActionLoading("");
    }
  };

  const uiCss = `
    .reservations-container.ops-pageShell{padding:10px}
    .reservations-container .ops-shell{background:linear-gradient(180deg,rgba(25,27,53,.94),rgba(11,12,25,.97));border:1px solid rgba(255,255,255,.08);border-radius:22px;box-shadow:0 24px 64px rgba(3,7,18,.32);overflow:hidden}
    .reservations-container .ops-header{display:flex;justify-content:space-between;align-items:flex-end;gap:18px;padding:18px 18px 14px;border-bottom:1px solid rgba(255,255,255,.08);flex-wrap:wrap}
    .reservations-container .ops-title{margin:0;font-size:1.38rem;font-weight:800;letter-spacing:-.02em;color:rgba(244,248,255,.98)}
    .reservations-container .ops-subtitle{margin:6px 0 0;font-size:.82rem;line-height:1.55;color:rgba(214,226,247,.68);max-width:760px}
    .reservations-container .ops-bu{margin-top:8px;font-size:.76rem;color:rgba(214,226,247,.62)}
    .reservations-container .ops-actions{display:flex;gap:8px;flex-wrap:wrap}
    .reservations-container .ops-toolbarBtn{border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.04);color:rgba(244,248,255,.92);border-radius:10px;padding:8px 12px;font-size:.78rem;font-weight:700;display:inline-flex;align-items:center;gap:7px}
    .reservations-container .ops-toolbarBtn:disabled{opacity:.45;cursor:not-allowed}
    .reservations-container .ops-notice{margin:14px 18px 0;padding:12px 14px;border-radius:14px;font-size:.78rem;line-height:1.55;border:1px solid rgba(255,255,255,.08)}
    .reservations-container .ops-notice--success{background:rgba(25,164,122,.16);color:rgba(176,246,220,.98);border-color:rgba(25,164,122,.32)}
    .reservations-container .ops-notice--error{background:rgba(177,48,69,.22);color:rgba(255,213,213,.98);border-color:rgba(255,125,147,.32)}
    .reservations-container .ops-body{display:flex;flex-direction:column;gap:12px;padding:14px 18px 18px}
    .reservations-container .ops-tabs{display:flex;gap:8px;flex-wrap:wrap}
    .reservations-container .ops-tab{border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.03);color:rgba(214,226,247,.78);border-radius:999px;padding:9px 14px;font-size:.76rem;font-weight:700;display:inline-flex;align-items:center;gap:8px}
    .reservations-container .ops-tab.is-active{background:linear-gradient(135deg,rgba(48,216,255,.24),rgba(168,85,247,.22));border-color:rgba(108,223,255,.38);color:rgba(247,250,255,.98)}
    .reservations-container .ops-tabCount{display:inline-flex;align-items:center;justify-content:center;min-width:22px;height:22px;padding:0 6px;border-radius:999px;background:rgba(255,255,255,.09);font-size:.72rem}
    .reservations-container .ops-filters{display:flex;gap:10px;align-items:center;flex-wrap:wrap}
    .reservations-container .ops-search{position:relative;min-width:260px;flex:1}
    .reservations-container .ops-search svg{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:rgba(214,226,247,.45)}
    .reservations-container .ops-input,.reservations-container .ops-select{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.14);border-radius:10px;color:rgba(244,248,255,.94);height:38px;padding:0 12px;font-size:.78rem}
    .reservations-container .ops-input{width:100%;padding-left:36px}
    .reservations-container .ops-date{width:150px}
    .reservations-container .ops-tableWrap{border:1px solid rgba(255,255,255,.08);border-radius:18px;overflow:auto;background:rgba(7,10,20,.36)}
    .reservations-container .ops-table{width:100%;min-width:1060px;border-collapse:separate;border-spacing:0;table-layout:fixed}
    .reservations-container .ops-table th{padding:12px 16px;text-align:left;font-size:.68rem;letter-spacing:.08em;text-transform:uppercase;color:rgba(214,226,247,.52);border-bottom:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);overflow-wrap:anywhere;word-break:break-word;white-space:normal}
    .reservations-container .ops-table td{padding:14px 16px;border-bottom:1px solid rgba(255,255,255,.06);font-size:.8rem;vertical-align:top;color:rgba(230,236,247,.88);overflow-wrap:anywhere;word-break:break-word;white-space:normal}
    .reservations-container .ops-table tr:hover td{background:rgba(255,255,255,.02)}
    .reservations-container .ops-cellTitle{font-size:.84rem;font-weight:700;color:rgba(245,248,255,.97);overflow-wrap:anywhere;word-break:break-word}
    .reservations-container .ops-cellMeta{margin-top:4px;font-size:.72rem;line-height:1.55;color:rgba(214,226,247,.62);overflow-wrap:anywhere;word-break:break-word}
    .reservations-container .ops-excerpt{line-height:1.6;overflow-wrap:anywhere;word-break:break-word}
    .reservations-container .ops-colSelect{width:48px}
    .reservations-container .ops-colDate{width:132px}
    .reservations-container .ops-colGuest{width:180px}
    .reservations-container .ops-colChannel{width:150px}
    .reservations-container .ops-colPriority{width:118px}
    .reservations-container .ops-colReply{width:124px}
    .reservations-container .ops-colAnalysis{width:132px}
    .reservations-container .ops-colOpen{width:112px}
    .reservations-container .ops-checkboxCell{padding-left:12px;padding-right:8px}
    .reservations-container .ops-checkboxWrap{display:flex;justify-content:center;align-items:center}
    .reservations-container .ops-checkbox{width:16px;height:16px;accent-color:#8b5cf6;cursor:pointer}
    .reservations-container .ops-chip{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;padding:5px 10px;font-size:.68rem;font-weight:800;white-space:nowrap}
    .reservations-container .ops-chip--neutral{background:rgba(255,255,255,.08);color:rgba(212,224,244,.95)}
    .reservations-container .ops-chip--success{background:rgba(25,164,122,.25);color:rgba(176,246,220,.98)}
    .reservations-container .ops-chip--warning{background:rgba(255,193,95,.14);color:rgba(255,230,185,.98)}
    .reservations-container .ops-chip--danger{background:rgba(177,48,69,.24);color:rgba(255,204,204,.98)}
    .reservations-container .ops-chip--muted{background:rgba(255,255,255,.05);color:rgba(214,226,247,.65)}
    .reservations-container .ops-priority{font-weight:800;color:rgba(244,248,255,.96)}
    .reservations-container .ops-openBtn{padding:6px 10px;font-size:.76rem;display:inline-flex;align-items:center;gap:6px}
    .reservations-container .ops-empty{padding:28px;text-align:center;font-size:.82rem;line-height:1.7;color:rgba(214,226,247,.72)}
    .reservations-container .ops-pagination{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;padding:0 4px}
    .reservations-container .ops-pageText{font-size:.72rem;color:rgba(230,230,247,.62)}
    .reservations-container .ops-pageControls{display:flex;gap:8px;align-items:center}
    .reservations-container .ops-pageBtn{border:1px solid rgba(255,255,255,.22);color:rgba(230,230,247,.78);background:transparent;padding:4px 8px;border-radius:6px;font-size:.72rem;font-weight:600;line-height:1.2}
    .reservations-container .ops-pageBtn:disabled{cursor:not-allowed;opacity:.35}
    .ops-modalBackdrop{position:fixed;inset:0;background:rgba(4,8,18,.68);backdrop-filter:blur(8px);display:flex;justify-content:center;align-items:flex-start;padding:34px 16px;z-index:1200;overflow-y:auto}
    .ops-modal{width:min(1180px,100%);border-radius:24px;border:1px solid transparent;background:linear-gradient(180deg,rgba(24,24,48,.98),rgba(10,10,22,.98)) padding-box,linear-gradient(135deg,rgba(48,216,255,.45),rgba(168,85,247,.50),rgba(255,77,79,.28)) border-box;box-shadow:0 32px 80px rgba(10,17,20,.46);overflow:hidden;color:rgba(229,237,250,.92)}
    .ops-modalHeader{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;padding:22px 24px;border-bottom:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.02)}
    .ops-modalTitle{margin:0;font-size:1.42rem;font-weight:800;color:rgba(244,248,255,.97)}
    .ops-modalSubtitle{margin:8px 0 0;font-size:.84rem;line-height:1.6;color:rgba(214,226,247,.68);max-width:880px}
    .ops-iconBtn{width:40px;height:40px;border-radius:12px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.04);color:rgba(230,236,247,.92);display:inline-flex;justify-content:center;align-items:center;cursor:pointer;font-size:18px}
    .ops-modalBody{padding:22px 24px 26px}
    .ops-detail{display:flex;flex-direction:column;gap:20px}
    .ops-summary{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px}
    .ops-card{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:18px;padding:16px}
    .ops-cardLabel{font-size:.68rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:rgba(214,226,247,.62)}
    .ops-cardValue{margin-top:10px;font-size:1rem;font-weight:800;color:rgba(244,248,255,.96);overflow-wrap:anywhere;word-break:break-word}
    .ops-cardMeta{margin-top:6px;font-size:.8rem;color:rgba(214,226,247,.68);line-height:1.5;overflow-wrap:anywhere;word-break:break-word}
    .ops-grid{display:grid;grid-template-columns:1.2fr .8fr;gap:16px}
    .ops-sectionTitle{margin:0 0 10px;font-size:1rem;font-weight:800;color:rgba(245,248,255,.97)}
    .ops-sectionText{margin:0;font-size:.84rem;line-height:1.65;color:rgba(218,229,247,.78);white-space:pre-wrap;overflow-wrap:anywhere;word-break:break-word}
    .ops-textarea{width:100%;min-height:150px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.14);border-radius:14px;color:rgba(244,248,255,.94);padding:12px 14px;font-size:.82rem;line-height:1.6;resize:vertical}
    .ops-help{margin-top:8px;font-size:.76rem;line-height:1.55;color:rgba(214,226,247,.62)}
    .ops-actionsRow{display:flex;gap:10px;flex-wrap:wrap}
    .ops-details summary{cursor:pointer;font-size:.82rem;font-weight:800;color:rgba(220,210,255,.96)}
    .ops-code{margin-top:12px;border-radius:16px;background:rgba(6,9,18,.88);color:#f4f1ea;padding:16px;font-size:.74rem;line-height:1.6;overflow:auto;white-space:pre-wrap;word-break:break-word;max-height:280px;border:1px solid rgba(255,255,255,.08)}
    @media (max-width:1100px){.reservations-container .ops-filters{align-items:stretch}.reservations-container .ops-search{min-width:0;width:100%}.ops-summary{grid-template-columns:repeat(2,minmax(0,1fr))}.ops-grid{grid-template-columns:1fr}}
    @media (max-width:860px){.reservations-container.ops-pageShell{padding:8px}.reservations-container .ops-header,.reservations-container .ops-filters,.reservations-container .ops-pagination,.ops-modalHeader{flex-direction:column;align-items:stretch}.reservations-container .ops-date,.reservations-container .ops-select{width:100%}.ops-summary{grid-template-columns:1fr}.ops-modalBody,.ops-modalHeader{padding:18px}}
  `;

  return (
    <div className="reservations-container ops-pageShell">
      <style>{uiCss}</style>

      <section className="ops-shell">
        <div className="ops-header">
          <div>
            <h1 className="ops-title">Review Operations</h1>
            <p className="ops-subtitle">
              Work through the reviews that need an answer, the serious complaints that need
              attention and the reviews that should be analyzed again. The list stays in the
              center; the actions happen when you open a review.
            </p>
            {businessUnit?.name ? (
              <div className="ops-bu">
                Current hotel: <strong>{businessUnit.name}</strong>
              </div>
            ) : null}
          </div>

          <div className="ops-actions">
            <button
              type="button"
              className="ops-toolbarBtn"
              onClick={handleBulkAnalyze}
              disabled={!selectedReviewIds.length || Boolean(actionLoading) || loading || refreshing}
            >
              <FiRotateCw />
              {actionLoading === "bulk_ai" ? "Sending..." : `${bulkAnalyzeLabel}${selectedReviewIds.length ? ` (${selectedReviewIds.length})` : ""}`}
            </button>
            <button
              type="button"
              className="ops-toolbarBtn"
              onClick={() => fetchQueue({ tab: activeTab, page: pagination.page || 1, silent: true })}
              disabled={refreshing || loading}
            >
              <FiRefreshCw />
              {refreshing ? "Refreshing..." : "Refresh queue"}
            </button>
          </div>
        </div>

        {feedback ? <div className="ops-notice ops-notice--success">{feedback}</div> : null}
        {error ? <div className="ops-notice ops-notice--error">{error}</div> : null}

        <div className="ops-body">
          <div className="ops-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`ops-tab ${activeTab === tab.key ? "is-active" : ""}`}
                onClick={() => handleTabChange(tab.key)}
              >
                <span>{tab.label}</span>
                <span className="ops-tabCount">{tabCounts?.[tab.key] || 0}</span>
              </button>
            ))}
          </div>

          <div className="ops-filters">
            <label className="ops-search">
              <FiSearch />
              <input
                className="ops-input"
                type="text"
                placeholder="Search guest, channel or review text"
                value={filters.search}
                onChange={handleFilterChange("search")}
              />
            </label>

            <select className="ops-select" value={filters.source_id} onChange={handleFilterChange("source_id")}>
              <option value="all">All channels</option>
              {sources.map((source) => (
                <option key={source.id} value={String(source.id)}>
                  {source.name}
                </option>
              ))}
            </select>

            <select className="ops-select" value={filters.reply_priority} onChange={handleFilterChange("reply_priority")}>
              <option value="all">Any priority</option>
              <option value="high">High priority</option>
              <option value="medium">Medium priority</option>
              <option value="low">Low priority</option>
            </select>

            <select className="ops-select" value={filters.ai_status} onChange={handleFilterChange("ai_status")}>
              <option value="all">Any analysis state</option>
              <option value="done">Ready</option>
              <option value="processing">In progress</option>
              <option value="pending">Waiting</option>
              <option value="error">Needs review</option>
            </select>

            <input className="ops-input ops-date" type="date" value={filters.date_from} onChange={handleFilterChange("date_from")} />
            <input className="ops-input ops-date" type="date" value={filters.date_to} onChange={handleFilterChange("date_to")} />

            <button type="button" className="ops-toolbarBtn" onClick={applyFilters}>
              Apply
            </button>
          </div>

          <div className="ops-tableWrap">
            {loading ? (
              <div className="ops-empty">Loading review operations...</div>
            ) : reviews.length ? (
              <table className="ops-table">
                <colgroup>
                  <col className="ops-colSelect" />
                  <col className="ops-colDate" />
                  <col className="ops-colGuest" />
                  <col className="ops-colChannel" />
                  <col />
                  <col className="ops-colPriority" />
                  <col className="ops-colReply" />
                  <col className="ops-colAnalysis" />
                  <col className="ops-colOpen" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="ops-checkboxCell">
                      <div className="ops-checkboxWrap">
                        <input
                          ref={selectAllRef}
                          className="ops-checkbox"
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleSelectAllVisible}
                          aria-label="Select all reviews on this page"
                        />
                      </div>
                    </th>
                    <th>Date</th>
                    <th>Guest</th>
                    <th>Channel</th>
                    <th>What needs attention</th>
                    <th>Priority</th>
                    <th>Reply</th>
                    <th>Analysis</th>
                    <th>Open</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((review) => (
                    <tr key={review.id}>
                      <td className="ops-checkboxCell">
                        <div className="ops-checkboxWrap">
                          <input
                            className="ops-checkbox"
                            type="checkbox"
                            checked={selectedReviewIds.includes(review.id)}
                            onChange={() => toggleReviewSelection(review.id)}
                            aria-label={`Select review ${review.id}`}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="ops-cellTitle">{formatDate(review.review_date || review.created_at)}</div>
                        <div className="ops-cellMeta">
                          Criticality: {formatCriticality(review.critical_points_score)}
                        </div>
                      </td>
                      <td>
                        <div className="ops-cellTitle">
                          {review.guest_display_name || review.guest_name || "Anonymous guest"}
                        </div>
                        <div className="ops-cellMeta">
                          Score: {formatScore(review.rating_value, review.rating_scale_max)}
                        </div>
                      </td>
                      <td>
                        <div className="ops-cellTitle">{review?.source?.name || "Unknown channel"}</div>
                        <div className="ops-cellMeta">
                          Urgency: {formatCriticality(review.urgency_score)}
                        </div>
                      </td>
                      <td>
                        <div className="ops-excerpt">{buildExcerpt(review)}</div>
                        {review.latest_suggested_internal_action ? (
                          <div className="ops-cellMeta">{review.latest_suggested_internal_action}</div>
                        ) : null}
                      </td>
                      <td>
                        <div className="ops-priority">{formatPriority(review.reply_priority)}</div>
                        <div className="ops-cellMeta">
                          {review.reply_draft_available ? "Draft already saved" : "No draft yet"}
                        </div>
                      </td>
                      <td>
                        <span className={buildToneClass("ops-chip", getReplyTone(review.reply_status))}>
                          {getReplyLabel(review.reply_status)}
                        </span>
                        {review.should_reply ? (
                          <div className="ops-cellMeta">Worth checking soon</div>
                        ) : null}
                      </td>
                      <td>
                        <span className={buildToneClass("ops-chip", getAnalysisTone(review.ai_queue_status))}>
                          {getAnalysisLabel(review.ai_queue_status)}
                        </span>
                        {review.ai_trigger_reason ? (
                          <div className="ops-cellMeta">{review.ai_trigger_reason.replace(/_/g, " ")}</div>
                        ) : null}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="rm-topBtn rm-topBtn--violet ops-openBtn"
                          onClick={() => handleOpenDetail(review.id)}
                        >
                          <FiEye />
                          Open
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="ops-empty">
                No reviews match this queue right now.
                <br />
                Try another tab or adjust the filters.
              </div>
            )}
          </div>

          <div className="ops-pagination">
            <div className="ops-pageText">{currentRangeText}</div>
            <div className="ops-pageControls">
              <button
                type="button"
                className="ops-pageBtn"
                onClick={() => handlePageChange((pagination.page || 1) - 1)}
                disabled={(pagination.page || 1) <= 1 || refreshing}
              >
                <FiChevronLeft />
              </button>
              <span className="ops-pageText">
                Page {pagination.page || 1} of {pagination.total_pages || 1}
              </span>
              <button
                type="button"
                className="ops-pageBtn"
                onClick={() => handlePageChange((pagination.page || 1) + 1)}
                disabled={(pagination.page || 1) >= (pagination.total_pages || 1) || refreshing}
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Modal
        open={Boolean(selectedReviewId)}
        onClose={closeDetail}
        title={selectedReviewDetail?.guest_display_name || selectedReviewDetail?.guest_name || "Review detail"}
        subtitle="Review the guest message, the current recommendation and the operational action you want to take from here."
      >
        {detailLoading ? <div className="ops-empty">Loading review detail...</div> : null}
        {!detailLoading && detailError ? (
          <div className="ops-notice ops-notice--error" style={{ margin: 0 }}>
            {detailError}
          </div>
        ) : null}

        {!detailLoading && !detailError && selectedReviewDetail ? (
          <div className="ops-detail">
            <div className="ops-summary">
              <article className="ops-card">
                <div className="ops-cardLabel">Channel</div>
                <div className="ops-cardValue">{selectedReviewDetail?.source?.name || "Unknown channel"}</div>
                <div className="ops-cardMeta">
                  Review date: {formatDate(selectedReviewDetail.review_date || selectedReviewDetail.created_at)}
                </div>
              </article>
              <article className="ops-card">
                <div className="ops-cardLabel">Score</div>
                <div className="ops-cardValue">
                  {formatScore(selectedReviewDetail.rating_value, selectedReviewDetail.rating_scale_max)}
                </div>
                <div className="ops-cardMeta">
                  Normalized: {selectedReviewDetail.normalized_rating_100 ? `${Math.round(Number(selectedReviewDetail.normalized_rating_100))}/100` : "Pending"}
                </div>
              </article>
              <article className="ops-card">
                <div className="ops-cardLabel">Reply status</div>
                <div className="ops-cardValue">{getReplyLabel(selectedReviewDetail.reply_status)}</div>
                <div className="ops-cardMeta">
                  Priority: {formatPriority(selectedReviewDetail.reply_priority)}
                </div>
              </article>
              <article className="ops-card">
                <div className="ops-cardLabel">Analysis state</div>
                <div className="ops-cardValue">{getAnalysisLabel(selectedReviewDetail?.ai_processing_queue?.queue_status)}</div>
                <div className="ops-cardMeta">
                  Criticality: {formatCriticality(selectedReviewDetail.critical_points_score)}
                </div>
              </article>
            </div>

            <div className="ops-grid">
              <article className="ops-card">
                <h3 className="ops-sectionTitle">Guest review</h3>
                {selectedReviewDetail.title ? (
                  <p className="ops-sectionText">
                    <strong>{selectedReviewDetail.title}</strong>
                  </p>
                ) : null}
                {selectedReviewDetail.positive_text ? (
                  <p className="ops-sectionText">
                    <strong>What went well:</strong>
                    {"\n"}
                    {selectedReviewDetail.positive_text}
                  </p>
                ) : null}
                {selectedReviewDetail.negative_text ? (
                  <p className="ops-sectionText">
                    <strong>What needs improvement:</strong>
                    {"\n"}
                    {selectedReviewDetail.negative_text}
                  </p>
                ) : null}
                {selectedReviewDetail.review_text ? (
                  <p className="ops-sectionText">{selectedReviewDetail.review_text}</p>
                ) : null}
              </article>

              <article className="ops-card">
                <h3 className="ops-sectionTitle">System reading</h3>
                <p className="ops-sectionText">
                  {selectedReviewDetail?.latest_ai_analysis?.manager_summary_text ||
                    selectedReviewDetail?.latest_ai_analysis?.summary_text ||
                    "The review has not been summarized yet."}
                </p>
                {selectedReviewDetail?.latest_ai_analysis?.suggested_internal_action ? (
                  <p className="ops-sectionText">
                    <strong>Suggested internal action:</strong>
                    {"\n"}
                    {selectedReviewDetail.latest_ai_analysis.suggested_internal_action}
                  </p>
                ) : null}
                {Array.isArray(selectedReviewDetail?.topics) && selectedReviewDetail.topics.length ? (
                  <p className="ops-sectionText">
                    <strong>Detected topics:</strong>
                    {"\n"}
                    {selectedReviewDetail.topics
                      .map((topic) => topic?.topic?.label || topic?.topic?.code)
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                ) : null}
              </article>
            </div>

            <article className="ops-card">
              <h3 className="ops-sectionTitle">Reply draft</h3>
              <textarea
                className="ops-textarea"
                value={replyDraft}
                onChange={(event) => setReplyDraft(event.target.value)}
                placeholder="Write the answer you want to save or post. You can start from the AI suggestion and adapt it."
              />
              <div className="ops-help">
                Save the draft first if you want it ready for later. Mark the review as answered when the reply has already been posted on the external platform or handled by the team.
              </div>
            </article>

            <article className="ops-card">
              <h3 className="ops-sectionTitle">Internal notes</h3>
              <textarea
                className="ops-textarea"
                style={{ minHeight: 110 }}
                value={workflowNotes}
                onChange={(event) => setWorkflowNotes(event.target.value)}
                placeholder="Optional note for the team, for example why the recommendation was dismissed or how the guest was handled."
              />
            </article>

            <div className="ops-actionsRow">
              <button
                type="button"
                className="ops-toolbarBtn"
                onClick={() => runWorkflowAction("save_draft", {}, "Reply draft saved.")}
                disabled={Boolean(actionLoading)}
              >
                <FiMessageSquare />
                {actionLoading === "save_draft" ? "Saving..." : "Save draft"}
              </button>
              <button
                type="button"
                className="ops-toolbarBtn"
                onClick={() =>
                  runWorkflowAction("mark_responded", { reply_text: replyDraft }, "Review marked as answered.")
                }
                disabled={Boolean(actionLoading)}
              >
                <FiSend />
                {actionLoading === "mark_responded" ? "Saving..." : "Mark as answered"}
              </button>
              <button
                type="button"
                className="ops-toolbarBtn"
                onClick={() => runWorkflowAction("dismiss", {}, "Recommendation dismissed.")}
                disabled={Boolean(actionLoading)}
              >
                <FiSlash />
                {actionLoading === "dismiss" ? "Saving..." : "Dismiss recommendation"}
              </button>
              <button
                type="button"
                className="ops-toolbarBtn"
                onClick={handleAnalyzeNow}
                disabled={Boolean(actionLoading)}
              >
                <FiRotateCw />
                {actionLoading === "reanalyze"
                  ? "Analyzing..."
                  : selectedReviewDetail?.latest_ai_analysis?.id
                    ? "Reanalyze now"
                    : "Analyze now"}
              </button>
            </div>

            <details className="ops-details">
              <summary>Source payload and structured analysis</summary>
              <div className="ops-code">{formatJson(selectedReviewDetail.raw_payload)}</div>
              {selectedReviewDetail?.latest_ai_analysis?.structured_output ? (
                <div className="ops-code">
                  {formatJson(selectedReviewDetail.latest_ai_analysis.structured_output)}
                </div>
              ) : null}
            </details>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

export default ReviewOperationsPage;
